import {
  FC,
  ReactNode,
  useState,
  useEffect,
  useRef,
  useMemo,
  MutableRefObject,
} from 'react';
// 组件
import {
  ProTooltip,
  ProDropdown,
  ProSpace,
  ProPopover,
  ProCheckbox,
  ProRow,
  ProCol,
  ProMessage,
} from '../../antd';
import {
  SettingOutlined,
  ReloadOutlined,
  ColumnHeightOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import CopyColumnsModal from './copyColumnsModal';
import CopyPreviewModal from './copyPreviewModal';
// 方法
import { columnsToCopyColumns, formatColumns } from './utils';
import { useLayoutContext } from '../../PageLayout/context/LayoutContext';
import { flatArray, flatChildrenArray, getColumnValue } from '../../utils/dataUtils';
import { renderEnumNames } from '../../utils/renderUtils';
// 常量
import {
  tableSizeType,
  columnsProps,
  IFormatColumns,
  ListParamsProps,
} from '../interface';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { ModalTableActionType } from '../../constants/interface';
import { RowSelectionType } from 'antd/lib/table/interface';
import styles from './tableToolbar.module.less';
// 密度 刷新 设置 复制
export type toolBarType = 'density' | 'refresh' | 'set' | 'copy';
interface toolbarAction {
  reload: () => void;
}
export type toolbarActionType = toolbarAction | undefined;
export interface TableToolbarProps {
  /** 表格大小 */
  size: tableSizeType;
  /** 列配置 */
  columns: columnsProps[];
  toolbarRef?: MutableRefObject<toolbarActionType>;
  /** toolBar展示配置 */
  toolBar?: false | toolBarType[];
  /** 头部操作按钮 */
  headOperation?: false | ReactNode[];
  /** toolBar末尾操作按钮 */
  toolBarButtons?: false | ReactNode[];
  /** toolBar底部自定义内容 */
  toolBarFooterContent?: ReactNode;
  /** 列表数据 */
  dataSource?: any[];
  /** 可复制的字段 */
  copyColumns?: columnsProps[];
  /** 列表右上角是否显示帮助文档图标 */
  showHelpIcon?: boolean;
  /** TODO 复制选项是否需要去除重复数据 */
  defaultUnrepeated?: boolean;
  selectedRows?: any[];
  /** 是否为主子表 */
  isMainSubTable?: boolean;
  /** 是否为合并(行/列)表格 */
  isMergeTable?: boolean;
  /** 挂接子表数据的字段 */
  chilrenKey?: string;
  /** 选择框类型 */
  rowSelectionType?: RowSelectionType;
  /** 操作按钮之后节点 */
  headOperationAfter?: ReactNode;
  /** 修改表格密度回调 */
  onChangeSize?: (val: tableSizeType) => void;
  /** 刷新回调 */
  onRefresh?: () => void;
  onChangeColumns?: (checkedValue: any[]) => void;
  /** 获取列表参数 */
  getTableParams?: () => Promise<ListParamsProps>;
  /** 清空选中行 */
  onSelectEmpty?: () => void;
}
/** 表格toolBar */
const TableToolbar: FC<TableToolbarProps> = props => {
  const {
    toolBar,
    headOperation,
    toolBarButtons,
    toolBarFooterContent,
    columns,
    copyColumns,
    dataSource,
    selectedRows,
    defaultUnrepeated,
    isMainSubTable,
    isMergeTable,
    rowSelectionType,
    headOperationAfter,
    chilrenKey,
    onChangeSize,
    onRefresh,
    onChangeColumns,
    onSelectEmpty,
  } = props;
  const [draftsCount, setDraftsCount] = useState<string>();
  const [checked, setChecked] = useState<boolean>(true);
  const [groupValues, setGroupValues] = useState<any[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const actionRef = useRef<ModalTableActionType>();
  const copyColumnsModalRef = useRef<ModalTableActionType>();
  const copyPreviewModalRef = useRef<ModalTableActionType>();
  const { dispatch, enumData, } = useLayoutContext();
  useEffect(() => {
    const newGroupValues: any[] = [];
    columns.forEach((v, index) => {
      if (!v.isHide) newGroupValues.push(index);
    });
    setGroupValues(newGroupValues);
  }, [columns]);
  const currentCopyColumns = useMemo(() => {
    if (copyColumns) return copyColumns;
    return columnsToCopyColumns(columns);
  }, [columns, copyColumns]);
  const handleChangeSize = (item: any) => {
    const val = item.key;
    onChangeSize && onChangeSize(val);
  };
  const newColumns: IFormatColumns = formatColumns(columns);
  const handleChange = (checkedValue: any[]) => {
    setGroupValues(checkedValue);
    onChangeColumns?.(checkedValue);
  };
  const handleAllChange = (e: CheckboxChangeEvent) => {
    const { checked } = e.target;
    setChecked(checked);
    if (checked) {
      onChangeColumns?.(columns.map((_, index) => index));
    } else {
      onChangeColumns?.([]);
    }
  };
  
  // 点击表头复制按钮
  const handleCopy = () => {
    if (dataSource?.length) {
      copyColumnsModalRef.current?.show();
    } else {
      ProMessage.warning('暂无可复制数据!');
    }
  };
  // 列匹配完确定
  const handleColumnsOk = (
    values: any[],
    isUnrepeated: boolean,
    isCopyTitle: boolean,
    isMainData?: boolean,
    isSubData?: boolean,
  ) => {
    /** 复制字符串集合 */
    const copyRows: string[] = [];
    /** csv数据集合 */
    const csvRows: any[][] = [];
    const newCopyColumns = values.map(v => {
      const findCopyColumn = currentCopyColumns?.find(
        item => item.dataIndex === v,
      );
      return findCopyColumn?.title ?? '';
    });
    csvRows.push(newCopyColumns);
    if (isCopyTitle) {
      const columsCopyStr = newCopyColumns.join(`\t`);
      copyRows.push(columsCopyStr);
    }
    const copyDataSource = (selectedRows?.length
      ? selectedRows
      : dataSource) ?? [];
    let resultDataSource: any[] = [];
    if (isMainSubTable || isMergeTable) {
      // 无数据
      if (!isMainData && !isSubData) {
        resultDataSource = [];
      }
      // 主表数据
      if (isMainData && !isSubData) {
        resultDataSource = copyDataSource;
      }
      // 子表数据
      if (!isMainData && isSubData) {
        resultDataSource = flatChildrenArray(copyDataSource, chilrenKey);
      }
      // 主子表数据
      if (isMainData && isSubData) {
        resultDataSource = flatArray(copyDataSource, chilrenKey);
      }
    } else {
      resultDataSource = copyDataSource;
    }
    resultDataSource?.forEach((record, index) => {
      const newCopyColumns = values.map(v => {
        const findCopyColumn = currentCopyColumns?.find(
          item => item.dataIndex === v,
        );
        const columnValue = getColumnValue(record, v as string);
        const newVal = findCopyColumn?.formatCopyValue ? findCopyColumn.formatCopyValue(columnValue, record, index) : columnValue;
        if (findCopyColumn?.enumName && enumData) {
          const enumItem = enumData[`${findCopyColumn.enumName}`]?.items ?? [];
          return renderEnumNames(newVal, enumItem);
        }
        if (findCopyColumn?.renderType === 'boolean') {
          return newVal ? '是' : '否';
        }
        return newVal === undefined ? '' : newVal;
      });
      const columsCopyStr = newCopyColumns.join(`\t`);
      const csvRowsItem = newCopyColumns.map(v => {
        if (v?.includes?.(',')) {
          return `"${v}"`;
        }
        return v;
      });
      if (isUnrepeated) {
        if (!copyRows.includes(columsCopyStr)) {
          copyRows.push(columsCopyStr);
          csvRows.push(csvRowsItem);
        }
      } else {
        copyRows.push(columsCopyStr);
        csvRows.push(csvRowsItem);
      }
    });
    const copyText: string | undefined = copyRows?.join(`\r\n`);
    copyPreviewModalRef.current?.show({
      copyText,
      csvRows,
      rows: resultDataSource?.length,
    });
  };
  const renderOperation = (item: toolBarType) => {
    switch (item) {
      case 'density':
        return (
          <ProTooltip placement="top" title="密度" key={item}>
            <ProDropdown
              overlayStyle={{ width: 80 }}
              menu={{
                onClick: handleChangeSize,
                items: [
                  {
                    key: 'middle',
                    label: '默认',
                  },
                  {
                    key: 'large',
                    label: '宽松',
                  },
                  {
                    key: 'small',
                    label: '紧凑',
                  },
                ],
              }}
              trigger={['click']}
            >
              <ColumnHeightOutlined />
            </ProDropdown>
          </ProTooltip>
        );
      case 'refresh':
        return (
          <ProTooltip placement="top" title="刷新" key={item}>
            <ReloadOutlined />
          </ProTooltip>
        );
      case 'set':
        return (
          <ProTooltip placement="top" title="列设置" key={item}>
            <ProPopover
              content={
                <>
                  <div className={styles.top}>
                    <ProCheckbox onChange={handleAllChange} checked={checked}>
                      列展示
                    </ProCheckbox>
                  </div>
                  <ProCheckbox.Group
                    style={{ width: '100%', maxWidth: 125 }}
                    value={groupValues}
                    onChange={handleChange}
                  >
                    <ProRow>
                      {!!newColumns.left.length && (
                        <>
                          <div className={styles.title}>固定在左侧</div>
                          {newColumns.left.map((item, index) => (
                            <ProCol span={24} key={`left_${index}`}>
                              <ProCheckbox value={item.toolbarIndex}>
                                {item.title}
                              </ProCheckbox>
                            </ProCol>
                          ))}
                        </>
                      )}
                      {!!newColumns.content.length && (
                        <>
                          <div className={styles.title}>不固定</div>
                          {newColumns.content.map((item, index) => (
                            <ProCol span={24} key={`content_${index}`}>
                              <ProCheckbox value={item.toolbarIndex}>
                                {item.title}
                              </ProCheckbox>
                            </ProCol>
                          ))}
                        </>
                      )}
                      {!!newColumns.right.length && (
                        <>
                          <div className={styles.title}>固定在右侧</div>
                          {newColumns.right.map((item, index) => (
                            <ProCol span={24} key={`right_${index}`}>
                              <ProCheckbox value={item.toolbarIndex}>
                                {item.title}
                              </ProCheckbox>
                            </ProCol>
                          ))}
                        </>
                      )}
                    </ProRow>
                  </ProCheckbox.Group>
                </>
              }
              trigger="click"
              open={visible}
              placement="bottomRight"
              arrow={true}
              onOpenChange={(visible: boolean) => setVisible(visible)}
            >
              <SettingOutlined />
            </ProPopover>
          </ProTooltip>
        );
      case 'copy':
        return (
          <ProTooltip placement="top" title="复制" key={item}>
            <CopyOutlined onClick={handleCopy} />
          </ProTooltip>
        );
      default:
        return null;
    }
  };
  const handleClick = (item: toolBarType) => {
    switch (item) {
      case 'density':
        break;
      case 'refresh':
        onRefresh && onRefresh();
        break;
      case 'set':
        break;
      case 'copy':
        break;
    }
  };

  const handleClickDrafts = () => {
    actionRef.current?.show();
  };
  if (!(headOperation || toolBar)) return null;

  return (
    <>
      <div className="yb-table-head">
        <div>
          <ProSpace size="middle" align="center">
            {Array.isArray(headOperation) && headOperation}
            {headOperationAfter}
            {
              rowSelectionType === 'checkbox' && !!selectedRows?.length && (
                <div className='select-row-config'>
                  <span>已选择</span>
                  <span className='select-row-config-num'>{selectedRows?.length}</span>
                  <span>项</span>
                  <span className='select-row-config-empty' onClick={onSelectEmpty}>清空</span>
                </div>
              ) 
            }
          </ProSpace>
        </div>
        {toolBar !== false && (
          <div className="flex-end-start">
            <div className="flex-end-start">
              {Array.isArray(toolBar) &&
                toolBar.map(item => (
                  <span className="yb-table-head-icon" key={item}>
                    <span onClick={() => handleClick(item)}>
                      {renderOperation(item)}
                    </span>
                  </span>
                ))}
            </div>
            {toolBarButtons && <div>{toolBarButtons}</div>}
          </div>
        )}
      </div>
      {!!toolBarFooterContent && <div>{toolBarFooterContent}</div>}
      <CopyColumnsModal
        copyColumnsModalRef={copyColumnsModalRef}
        copyColumns={currentCopyColumns}
        defaultUnrepeated={defaultUnrepeated}
        isMainSubTable={isMainSubTable || isMergeTable}
        onOk={handleColumnsOk}
      />
      <CopyPreviewModal copyPreviewModalRef={copyPreviewModalRef} />
    </>
  );
};

TableToolbar.defaultProps = {
  // toolBar: ['density', 'refresh', 'set', 'copy'],
  headOperation: false,
  toolBarButtons: false,
  showHelpIcon: true,
};

export default TableToolbar;
